import React, {
	forwardRef,
	useState,
	ClipboardEvent,
	useEffect,
} from 'react';
import './InputCreditCard.css';
import { creditCardsInfo } from './CreditCardsInfo';
import { createProgressiveMask } from '../../../component-library/utils/utils';
import InputBase, { IInputProps } from '../../../component-library/atoms/InputBase/InputBase';

enum CardsMasking {
	default = 'XXXX - XXXX - XXXX - XXXX',
	amex = 'XXXX - XXXXXX - XXXXX'
}
interface ICreditCardInfo {
	name: string;
	securityCodelength: number;
	ccLength: number;
}
export interface IInputCreditCardProps extends IInputProps {
	/** This prop will receive a setter function and it will return a number indicating what the security code length is. */
	setSecurityCodeLength?: THandlerFunction;
}

const InputCreditCard = forwardRef(
	(
		{
			id,
			className = '',
			value = '',
			setValue = () => { },
			label = '',
			setSecurityCodeLength = () => { },
			invalidErrorMessage = '',
			onBlur,
			onFocus,
			...rest
		}: IInputCreditCardProps,
		ref: any,
	): JSX.Element => {
		const [ccInfo, setCCinfo] = useState<ICreditCardInfo>({ name: '', securityCodelength: 3, ccLength: 16 });
		const [maskObjects, setMaskObjects] = useState<Record<string, any>[]>();
		const [isVisible, setIsVisible] = useState(false);

		const isCcValid = (val) => {
			const raw = val.replace(/[^0-9]/g, '');
			if (
				raw.match(
					/^(3[47][0-9]{13}|(6541|6556)[0-9]{12}|389[0-9]{11}|3(?:0[0-5]|[68][0-9])[0-9]{11}|65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})|63[7-9][0-9]{13}|(?:2131|1800|35\d{3})\d{11}|9[0-9]{15}|(6304|6706|6709|6771)[0-9]{12,15}|(5019|5018|5020|5038|6304|6759|6761|6763)[0-9]{8,15}|(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))|(6334|6767)[0-9]{12}|(6334|6767)[0-9]{14}|(6334|6767)[0-9]{15}|(4903|4905|4911|4936|6333|6759)[0-9]{12}|(4903|4905|4911|4936|6333|6759)[0-9]{14}|(4903|4905|4911|4936|6333|6759)[0-9]{15}|564182[0-9]{10}|564182[0-9]{12}|564182[0-9]{13}|633110[0-9]{10}|633110[0-9]{12}|633110[0-9]{13}|(62[0-9]{14,17})|4[0-9]{12}(?:[0-9]{3})?|(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}))$/,
				)
			) {
				return true;
			}
			return false;
		};

		const configureMaskingValues = (value: string) => {
			const cardInfo = (creditCardsInfo(value));
			setCCinfo(cardInfo);
			const mask = cardInfo.name === 'amex' ? 'amex' : "default"
			setMaskObjects(createProgressiveMask(CardsMasking[mask], "0", isVisible ? undefined : '*'))
			setSecurityCodeLength &&
				setSecurityCodeLength(cardInfo.securityCodelength);
		}

		const pasteHandler = (e: ClipboardEvent<HTMLInputElement>) => {
			const text = e.clipboardData.getData("text/plain")
			configureMaskingValues(text)
		};

		useEffect(() => {
			if (value.length <= 2) {
				configureMaskingValues(value)
			}
		}, [value]);

		useEffect(() => {
			configureMaskingValues(value);
		}, [isVisible]);

		return (
			<InputBase
				{...rest}
				id={id || "ccard"}
				label={label}
				ref={ref}
				onPaste={pasteHandler}
				className={`InputCreditCard ${className}`}
				value={value}
				minLength={0}
				isValid={isCcValid}
				invalidErrorMessage={
					invalidErrorMessage ||
					`Please enter a ${ccInfo.name === 'others' &&
						![15, 16].includes(value.length)
						? '15- or 16-digit'
						: value.length !== ccInfo.ccLength
							? `${ccInfo.ccLength.toString()}-digit`
							: 'valid credit'
					} card number`
				}
				setValue={(ccard) => {
					setValue(ccard)
				}}
				onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
					onBlur && onBlur(e);
				}}
				onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
					onFocus && onFocus(e);
				}}
				autoComplete="off"
				mask={maskObjects}
				setIsVisible={setIsVisible}
				isVisible={isVisible}
			/>
		);
	},
);

InputCreditCard.displayName = 'InputCreditCard';

export default InputCreditCard;
